import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowLeft, AlertCircle, Building2, Download, Layers, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import ExcelJS from 'exceljs';
import { reportsApi } from '@/api/client';
import type {
  ConsolidationReviewMatrix,
  ConsolidationReviewMatrixRowEntityValues,
  ConsolidationReviewSummary,
} from '@/types';

function parseAmt(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function fmtCell(n: number): string {
  if (n === 0) return '\u2014';
  const abs = Math.abs(n);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  if (n < 0) return `(${formatted})`;
  return formatted;
}

const GROUP_HEADER_BG: Record<string, string> = {
  local_input: 'bg-sky-100 text-sky-950',
  translated: 'bg-orange-100 text-orange-950',
  proportion: 'bg-pink-100 text-pink-950',
  ic: 'bg-emerald-100 text-emerald-950',
  nci: 'bg-cyan-100 text-cyan-950',
  contribution: 'bg-amber-100 text-amber-950',
};

const GROUP_CELL_BG: Record<string, string> = {
  local_input: 'bg-sky-50/80',
  translated: 'bg-orange-50/80',
  proportion: 'bg-pink-50/80',
  ic: 'bg-emerald-50/80',
  nci: 'bg-cyan-50/80',
  contribution: 'bg-amber-50/80',
};

const DETAIL_COLUMNS: Array<{
  key: keyof ConsolidationReviewMatrixRowEntityValues;
  label: string;
  groupKey: keyof typeof GROUP_CELL_BG;
}> = [
  { key: 'local_standalone', label: 'Local', groupKey: 'local_input' },
  { key: 'translated_standalone', label: 'Translated', groupKey: 'translated' },
  { key: 'proportion', label: 'Proportion', groupKey: 'proportion' },
  { key: 'ic_elimination', label: 'IC Elim', groupKey: 'ic' },
  { key: 'nci_elimination', label: 'NCI', groupKey: 'nci' },
  { key: 'contribution', label: 'Contribution', groupKey: 'contribution' },
];

function formatTimestamp(value: string | null): string {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

// ─── Tree types ───────────────────────────────────────────────────────────────

type EntityNumericValues = {
  local_standalone: number;
  translated_standalone: number;
  proportion: number;
  ic_elimination: number;
  nci_elimination: number;
  contribution: number;
};

type TreeNodeValues = {
  byEntity: Record<string, EntityNumericValues>;
  consolidated: number;
};

interface ConsolTreeNode {
  id: number;
  code: string;
  name: string;
  depth: number;
  children: ConsolTreeNode[];
  values: TreeNodeValues;
  isLeaf: boolean;
  accountType: string;
  isBalanceSheet: boolean;
}

// ─── buildAccountTree ─────────────────────────────────────────────────────────
// Converts the flat API rows into a parent→child tree using parent_id.
// Parent nodes start with zeroed values; aggregateNode() fills them in.

function buildAccountTree(
  rows: ConsolidationReviewMatrix['rows'],
  entityIds: number[],
): ConsolTreeNode[] {
  const accountRows = rows.filter((r) => !r.section_break);

  const nodeMap = new Map<number, ConsolTreeNode>();

  for (const row of accountRows) {
    const byEntity: Record<string, EntityNumericValues> = {};
    for (const eid of entityIds) {
      const ev = row.by_entity[String(eid)];
      byEntity[String(eid)] = ev
        ? {
            local_standalone: parseAmt(ev.local_standalone),
            translated_standalone: parseAmt(ev.translated_standalone),
            proportion: parseAmt(ev.proportion),
            ic_elimination: parseAmt(ev.ic_elimination),
            nci_elimination: parseAmt(ev.nci_elimination),
            contribution: parseAmt(ev.contribution),
          }
        : {
            local_standalone: 0,
            translated_standalone: 0,
            proportion: 0,
            ic_elimination: 0,
            nci_elimination: 0,
            contribution: 0,
          };
    }
    nodeMap.set(row.group_account_id, {
      id: row.group_account_id,
      code: row.code,
      name: row.name,
      depth: 0,
      children: [],
      values: { byEntity, consolidated: parseAmt(row.consolidated) },
      isLeaf: true,
      accountType: row.account_type,
      isBalanceSheet: row.is_balance_sheet,
    });
  }

  // Wire up parent → child relationships (preserves backend sort order)
  const roots: ConsolTreeNode[] = [];
  for (const row of accountRows) {
    const node = nodeMap.get(row.group_account_id);
    if (!node) continue;
    const pid = row.parent_id;
    if (pid != null && nodeMap.has(pid)) {
      const parent = nodeMap.get(pid)!;
      parent.children.push(node);
      parent.isLeaf = false;
    } else {
      roots.push(node);
    }
  }

  // Set depth on every node
  function setDepth(node: ConsolTreeNode, depth: number): void {
    node.depth = depth;
    for (const child of node.children) {
      setDepth(child, depth + 1);
    }
  }
  for (const root of roots) {
    setDepth(root, 0);
  }

  return roots;
}

// ─── aggregateNode ────────────────────────────────────────────────────────────
// Recursively sums child values up into parent nodes.
// Leaf nodes keep their own values unchanged.

function aggregateNode(node: ConsolTreeNode): TreeNodeValues {
  if (node.isLeaf) return node.values;

  const result: TreeNodeValues = { byEntity: {}, consolidated: 0 };

  for (const child of node.children) {
    const cv = aggregateNode(child);
    result.consolidated += cv.consolidated;
    for (const [eid, ev] of Object.entries(cv.byEntity)) {
      if (!result.byEntity[eid]) {
        result.byEntity[eid] = {
          local_standalone: 0,
          translated_standalone: 0,
          proportion: 0,
          ic_elimination: 0,
          nci_elimination: 0,
          contribution: 0,
        };
      }
      const t = result.byEntity[eid];
      t.local_standalone += ev.local_standalone;
      t.translated_standalone += ev.translated_standalone;
      t.proportion += ev.proportion;
      t.ic_elimination += ev.ic_elimination;
      t.nci_elimination += ev.nci_elimination;
      t.contribution += ev.contribution;
    }
  }

  node.values = result;
  return result;
}

// ─── filterTree ───────────────────────────────────────────────────────────────
// Keeps a node if it directly passes all active filters, OR any descendant
// does. Ancestor nodes of matching leaves are always shown with filtered
// children, preserving the account path context.

function isNodeMatch(
  node: ConsolTreeNode,
  q: string,
  showOnlyNonZero: boolean,
  showOnlyAdjusted: boolean,
): boolean {
  if (q && !node.code.toLowerCase().includes(q) && !node.name.toLowerCase().includes(q)) {
    return false;
  }
  if (showOnlyNonZero) {
    const { consolidated, byEntity } = node.values;
    const hasValue =
      consolidated !== 0 ||
      Object.values(byEntity).some((ev) => Object.values(ev).some((v) => v !== 0));
    if (!hasValue) return false;
  }
  if (showOnlyAdjusted) {
    const hasAdj = Object.values(node.values.byEntity).some(
      (ev) => ev.ic_elimination !== 0 || ev.nci_elimination !== 0 || ev.contribution !== 0,
    );
    if (!hasAdj) return false;
  }
  return true;
}

function anyVisible(
  node: ConsolTreeNode,
  q: string,
  showOnlyNonZero: boolean,
  showOnlyAdjusted: boolean,
): boolean {
  if (isNodeMatch(node, q, showOnlyNonZero, showOnlyAdjusted)) return true;
  return node.children.some((c) => anyVisible(c, q, showOnlyNonZero, showOnlyAdjusted));
}

function filterTree(
  nodes: ConsolTreeNode[],
  q: string,
  showOnlyNonZero: boolean,
  showOnlyAdjusted: boolean,
): ConsolTreeNode[] {
  return nodes
    .filter((n) => anyVisible(n, q, showOnlyNonZero, showOnlyAdjusted))
    .map((n) => ({
      ...n,
      children: filterTree(n.children, q, showOnlyNonZero, showOnlyAdjusted),
    }));
}

// ─── Client-side Excel export (ExcelJS) ──────────────────────────────────────
// Walks treeData (same source as UI) producing a styled workbook that mirrors
// the report: hierarchy, indentation, parent subtotals, section banners, and
// current visibleColumns selection.

// ARGB colour constants
const XL_TITLE_BG    = 'FF1E3A5F'; // dark navy  – title row
const XL_TITLE_FG    = 'FFFFFFFF';
const XL_BANNER_BG   = 'FF334155'; // slate-700  – section banners
const XL_BANNER_FG   = 'FFFFFFFF';
const XL_HDR1_BG     = 'FFE2E8F0'; // slate-200  – entity header row
const XL_HDR1_FG     = 'FF1E293B';
const XL_PARENT_BG   = 'FFF1F5F9'; // slate-100  – parent/group rows
const XL_LEAF_BG     = 'FFFFFFFF'; // white      – leaf rows
const XL_CONSOL_BG   = 'FFE5E7EB'; // gray-200   – consolidated column
const XL_NUM_FG      = 'FF1E293B';

// Sub-column header backgrounds matching UI group colours
const XL_COL_HDR_BG: Record<string, string> = {
  local_input:  'FFE0F2FE', // sky-100
  translated:   'FFFED7AA', // orange-200
  proportion:   'FFFCE7F3', // pink-100
  ic:           'FFD1FAE5', // emerald-100
  nci:          'FFCFFAFE', // cyan-100
  contribution: 'FFFEF3C7', // amber-100
};

// Data-cell tint per column group (subtle, not overwhelming)
const XL_COL_CELL_BG: Record<string, string> = {
  local_input:  'FFF0F9FF',
  translated:   'FFFEF6EE',
  proportion:   'FFFDF4FF',
  ic:           'FFF0FDF4',
  nci:          'FFF0FDFE',
  contribution: 'FFFEFCE8',
};

type XlFill = ExcelJS.Fill;
type XlFont = Partial<ExcelJS.Font>;
type XlAlign = Partial<ExcelJS.Alignment>;
type XlBorder = Partial<ExcelJS.Borders>;

function solidFill(argb: string): XlFill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

const THIN_BORDER: XlBorder = {
  top:    { style: 'thin', color: { argb: 'FFD1D5DB' } },
  bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  left:   { style: 'thin', color: { argb: 'FFD1D5DB' } },
  right:  { style: 'thin', color: { argb: 'FFD1D5DB' } },
};

function applyCell(
  cell: ExcelJS.Cell,
  opts: {
    value?: ExcelJS.CellValue;
    fill?: XlFill;
    font?: XlFont;
    alignment?: XlAlign;
    border?: XlBorder;
    numFmt?: string;
  },
): void {
  if (opts.value !== undefined) cell.value = opts.value;
  if (opts.fill)      cell.fill      = opts.fill;
  if (opts.font)      cell.font      = opts.font as ExcelJS.Font;
  if (opts.alignment) cell.alignment = opts.alignment as ExcelJS.Alignment;
  if (opts.border)    cell.border    = opts.border as ExcelJS.Borders;
  if (opts.numFmt)    cell.numFmt    = opts.numFmt;
}

async function buildExcel(
  treeData: ConsolTreeNode[],
  entities: ConsolidationReviewMatrix['entities'],
  visibleColumns: VisibleColumns,
  matrix: ConsolidationReviewMatrix,
): Promise<Blob> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Consolidation Review', {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 4 }],
  });

  const nCols     = visibleColumns.length;
  const totalCols = 1 + entities.length * nCols + 1; // account + entity cols + consolidated

  // ── Column widths ──
  ws.getColumn(1).width = 50; // account
  for (let c = 2; c <= totalCols; c++) {
    ws.getColumn(c).width = c === totalCols ? 18 : 15;
  }

  // Helper: fill an entire row with the same background
  function fillRow(rowNum: number, argb: string) {
    for (let c = 1; c <= totalCols; c++) {
      ws.getCell(rowNum, c).fill = solidFill(argb);
    }
  }

  // ── ROW 1: Report title ──
  ws.mergeCells(1, 1, 1, totalCols);
  applyCell(ws.getCell(1, 1), {
    value: `${matrix.title}  |  ${matrix.scenario}  |  ${matrix.period_label}  |  ${matrix.reporting_currency}`,
    fill:  solidFill(XL_TITLE_BG),
    font:  { bold: true, size: 13, color: { argb: XL_TITLE_FG } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  });
  ws.getRow(1).height = 28;

  // ── ROW 2: blank spacer ──
  fillRow(2, 'FFF8FAFC');
  ws.getRow(2).height = 6;

  // ── ROW 3: Entity group headers (entity name spans sub-columns) ──
  ws.getRow(3).height = 50;
  // "Account" spans rows 3-4
  ws.mergeCells(3, 1, 4, 1);
  applyCell(ws.getCell(3, 1), {
    value: 'Account',
    fill:  solidFill(XL_HDR1_BG),
    font:  { bold: true, size: 11, color: { argb: XL_HDR1_FG } },
    alignment: { horizontal: 'left', vertical: 'middle' },
    border: THIN_BORDER,
  });

  for (let ei = 0; ei < entities.length; ei++) {
    const ent = entities[ei];
    const sc = 2 + ei * nCols;
    if (nCols > 1) ws.mergeCells(3, sc, 3, sc + nCols - 1);
    applyCell(ws.getCell(3, sc), {
      value: `${ent.code}\n${ent.name}\n${ent.local_currency} → ${matrix.reporting_currency} @ ${ent.fx_rate_to_reporting}`,
      fill:  solidFill(XL_HDR1_BG),
      font:  { bold: true, size: 10, color: { argb: XL_HDR1_FG } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: THIN_BORDER,
    });
  }

  // "Consolidated" spans rows 3-4
  ws.mergeCells(3, totalCols, 4, totalCols);
  applyCell(ws.getCell(3, totalCols), {
    value: matrix.final_column.label,
    fill:  solidFill(XL_CONSOL_BG),
    font:  { bold: true, size: 10, color: { argb: XL_HDR1_FG } },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
    border: THIN_BORDER,
  });

  // ── ROW 4: Sub-column labels ──
  ws.getRow(4).height = 20;
  for (let ei = 0; ei < entities.length; ei++) {
    for (let ci = 0; ci < nCols; ci++) {
      const col = visibleColumns[ci];
      const c = 2 + ei * nCols + ci;
      applyCell(ws.getCell(4, c), {
        value: col.label,
        fill:  solidFill(XL_COL_HDR_BG[col.groupKey] ?? 'FFF3F4F6'),
        font:  { bold: true, size: 9, color: { argb: XL_HDR1_FG } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: THIN_BORDER,
      });
    }
  }

  // ── Number format for numeric cells ──
  const NUM_FMT = '#,##0;(#,##0);"-"';

  // ── Helper: write one tree node row ──
  function writeNode(node: ConsolTreeNode, depth: number): void {
    const rowNum = ws.rowCount + 1;
    const isParent = !node.isLeaf;
    const rowBg = isParent ? XL_PARENT_BG : XL_LEAF_BG;
    const fontOpts: XlFont = { bold: isParent, size: 10, color: { argb: XL_NUM_FG } };

    // Account cell
    applyCell(ws.getCell(rowNum, 1), {
      value: node.code ? `${node.code}  ${node.name}` : node.name,
      fill:  solidFill(rowBg),
      font:  { ...fontOpts, color: { argb: '1E293B' } },
      alignment: { horizontal: 'left', vertical: 'middle', indent: depth },
      border: THIN_BORDER,
    });

    // Entity numeric cells
    for (let ei = 0; ei < entities.length; ei++) {
      const ent = entities[ei];
      for (let ci = 0; ci < nCols; ci++) {
        const col = visibleColumns[ci];
        const c = 2 + ei * nCols + ci;
        const ev = node.values.byEntity[String(ent.id)];
        const val = ev ? (ev[col.key as keyof EntityNumericValues] ?? 0) : 0;
        const cellBg = isParent ? XL_PARENT_BG : (XL_COL_CELL_BG[col.groupKey] ?? XL_LEAF_BG);
        applyCell(ws.getCell(rowNum, c), {
          value: val !== 0 ? val : null,
          fill:  solidFill(cellBg),
          font:  fontOpts,
          alignment: { horizontal: 'center', vertical: 'middle' },
          border: THIN_BORDER,
          numFmt: NUM_FMT,
        });
      }
    }

    // Consolidated cell
    const consol = node.values.consolidated;
    applyCell(ws.getCell(rowNum, totalCols), {
      value: consol !== 0 ? consol : null,
      fill:  solidFill(isParent ? 'FFD1D5DB' : XL_CONSOL_BG),
      font:  { ...fontOpts, bold: true },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: THIN_BORDER,
      numFmt: NUM_FMT,
    });

    ws.getRow(rowNum).height = 18;
    ws.getRow(rowNum).outlineLevel = depth;

    // Recurse into children
    for (const child of node.children) writeNode(child, depth + 1);
  }

  // ── Helper: write a section banner row ──
  function writeBanner(label: string): void {
    const rowNum = ws.rowCount + 1;
    ws.mergeCells(rowNum, 1, rowNum, totalCols);
    applyCell(ws.getCell(rowNum, 1), {
      value: label,
      fill:  solidFill(XL_BANNER_BG),
      font:  { bold: true, size: 11, color: { argb: XL_BANNER_FG } },
      alignment: { horizontal: 'left', vertical: 'middle', indent: 1 },
    });
    ws.getRow(rowNum).height = 22;
  }

  // ── Write sections ──
  const bsRoots = treeData.filter((n) => n.isBalanceSheet);
  const isRoots = treeData.filter((n) => !n.isBalanceSheet);

  if (bsRoots.length > 0) {
    writeBanner('BALANCE SHEET — DETAIL');
    for (const root of bsRoots) writeNode(root, 0);
  }
  if (isRoots.length > 0) {
    writeBanner('INCOME STATEMENT — DETAIL');
    for (const root of isRoots) writeNode(root, 0);
  }

  // ── Write to browser Blob ──
  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface ConsolidationReviewPanelProps {
  /** Single period id from parent selector; omit when "All periods" or none selected. */
  periodId: string | null;
  /** When set, matrix columns are limited to this entity's org subtree (same as Run company). */
  scopeEntityId?: string;
}

export default function ConsolidationReviewPanel({
  periodId,
  scopeEntityId,
}: ConsolidationReviewPanelProps) {
  const [summaryData, setSummaryData] = useState<ConsolidationReviewSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [data, setData] = useState<ConsolidationReviewMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyNonZero, setShowOnlyNonZero] = useState(false);
  const [showOnlyAdjusted, setShowOnlyAdjusted] = useState(false);
  const [isDetailedView, setIsDetailedView] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  const effectiveScopeEntityId = scopeEntityId ?? selectedCompanyId ?? undefined;
  const isSummaryMode = !effectiveScopeEntityId;

  const toggleNode = useCallback((id: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const loadSummary = useCallback(async () => {
    if (!periodId) return;
    setSummaryLoading(true);
    setError(null);
    try {
      const res = await reportsApi.consolidationReviewSummary({
        period_id: periodId,
      });
      setSummaryData(res);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (e instanceof Error ? e.message : 'Failed to load consolidation review summary');
      setError(typeof msg === 'string' ? msg : 'Failed to load consolidation review summary');
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [periodId]);

  const loadMatrix = useCallback(async () => {
    if (!periodId || !effectiveScopeEntityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await reportsApi.consolidationReviewMatrix({
        period_id: periodId,
        scope_entity_id: effectiveScopeEntityId,
      });
      setData(res);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (e instanceof Error ? e.message : 'Failed to load consolidation review');
      setError(typeof msg === 'string' ? msg : 'Failed to load consolidation review');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [effectiveScopeEntityId, periodId]);

  useEffect(() => {
    if (!periodId) {
      setSummaryData(null);
      setData(null);
      setError(null);
      setSelectedCompanyId(null);
      return;
    }
    setError(null);
    setSearchTerm('');
    setShowOnlyAdjusted(false);
    setShowOnlyNonZero(false);
    setIsDetailedView(false);
    if (scopeEntityId) {
      setSelectedCompanyId(null);
    }
  }, [periodId, scopeEntityId]);

  // Columns visible per entity: all 6 in detailed mode, Contribution-only by default
  const visibleColumns = useMemo(
    () => (isDetailedView ? DETAIL_COLUMNS : DETAIL_COLUMNS.filter((c) => c.key === 'contribution')),
    [isDetailedView],
  );

  const theadRows = useMemo(() => {
    if (!data) return null;
    const { entities } = data;

    const row1: ReactNode[] = [
      <th
        key="acc"
        rowSpan={2}
        className="sticky left-0 z-30 min-w-[150px] max-w-[180px] border border-gray-200 bg-white px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
      >
        Account
      </th>,
    ];
    for (const ent of entities) {
      row1.push(
        <th
          key={`r1-ent-${ent.id}`}
          colSpan={visibleColumns.length}
          className="border border-gray-200 bg-slate-200 px-2 py-2 text-center"
        >
          <div className="flex flex-col items-center gap-0.5 leading-snug">
            <span className="text-xs font-bold text-slate-800">{ent.code}</span>
            <span className="text-[11px] font-normal text-slate-700">{ent.name}</span>
            <span className="text-[10px] font-normal text-slate-500">
              {ent.local_currency} → {data.reporting_currency} @ {ent.fx_rate_to_reporting}
            </span>
          </div>
        </th>,
      );
    }
    row1.push(
      <th
        key="final-h1"
        rowSpan={2}
        className="min-w-[120px] border border-gray-200 bg-gray-300 px-2 py-2 text-center text-xs font-bold text-gray-900"
      >
        {data.final_column.label}
      </th>,
    );

    const row2: ReactNode[] = [];
    for (const ent of entities) {
      for (const col of visibleColumns) {
        const bg = GROUP_HEADER_BG[col.groupKey] ?? 'bg-gray-100 text-gray-800';
        row2.push(
          <th
            key={`r2-${ent.id}-${col.key}`}
            className={clsx('border border-gray-200 px-1 py-1.5 text-center text-[10px] font-semibold leading-tight', bg)}
          >
            {col.label}
          </th>,
        );
      }
    }
    return { row1, row2 };
  }, [data, visibleColumns]);

  useEffect(() => {
    if (!periodId) {
      return;
    }
    if (isSummaryMode) {
      setData(null);
      void loadSummary();
      return;
    }
    void loadMatrix();
  }, [isSummaryMode, loadMatrix, loadSummary, periodId]);

  const selectedCompany = useMemo(() => {
    if (!summaryData || !selectedCompanyId) return null;
    for (const run of summaryData.runs) {
      const company = run.companies.find((item) => String(item.id) === selectedCompanyId);
      if (company) return company;
    }
    return null;
  }, [selectedCompanyId, summaryData]);

  // Build and aggregate the account hierarchy tree (memoised on raw data)
  const treeData = useMemo<ConsolTreeNode[]>(() => {
    if (!data) return [];
    const entityIds = data.entities.map((e) => e.id);
    const roots = buildAccountTree(data.rows, entityIds);
    for (const root of roots) {
      aggregateNode(root);
    }
    return roots;
  }, [data]);

  // Expand all root-level nodes when tree data first loads
  useEffect(() => {
    if (!treeData.length) return;
    setExpandedNodes(new Set(treeData.map((n) => n.id)));
  }, [treeData]);

  // Apply search / filter predicates; ancestors of matching nodes are kept
  const filteredTree = useMemo<ConsolTreeNode[]>(() => {
    if (!treeData.length) return [];
    const q = searchTerm.trim().toLowerCase();
    return filterTree(treeData, q, showOnlyNonZero, showOnlyAdjusted);
  }, [treeData, searchTerm, showOnlyNonZero, showOnlyAdjusted]);

  const handleExport = useCallback(async () => {
    if (!data || !treeData.length) return;
    setExporting(true);
    try {
      const blob = await buildExcel(treeData, data.entities, visibleColumns, data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consolidation_review_${data.period_label?.replace(/\s/g, '_') || 'export'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [data, treeData, visibleColumns]);

  const summary = useMemo(() => {
    if (!data) {
      return { nonZeroRows: 0, adjustedRows: 0, totalIc: 0, totalNci: 0 };
    }
    let nonZeroRows = 0;
    let adjustedRows = 0;
    let totalIc = 0;
    let totalNci = 0;
    for (const row of data.rows) {
      if (row.section_break) continue;
      const values = Object.values(row.by_entity);
      const rowIc = values.reduce((sum, ev) => sum + parseAmt(ev.ic_elimination), 0);
      const rowNci = values.reduce((sum, ev) => sum + parseAmt(ev.nci_elimination), 0);
      const rowHasNonZero = parseAmt(row.consolidated) !== 0
        || values.some((ev) => Object.values(ev).some((v) => parseAmt(v) !== 0));
      const rowHasAdjustment = rowIc !== 0 || rowNci !== 0
        || values.some((ev) => parseAmt(ev.contribution) !== 0);
      if (rowHasNonZero) nonZeroRows += 1;
      if (rowHasAdjustment) adjustedRows += 1;
      totalIc += rowIc;
      totalNci += rowNci;
    }
    return { nonZeroRows, adjustedRows, totalIc, totalNci };
  }, [data]);

  if (!periodId) {
    return (
      <div className="card p-8 text-center">
        <Layers className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <h3 className="text-base font-medium text-gray-900">Review</h3>
        <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
          Choose a <span className="font-medium text-gray-700">single period</span> in the header (not &quot;All periods&quot;) to
          load the FCCS-style consolidation review matrix for the latest completed run.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-gray-900">
        <Layers className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
        <h2 className="text-sm font-semibold">Consolidation Report</h2>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {summaryLoading && isSummaryMode && (
        <div className="card flex items-center justify-center gap-2 py-24 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          Loading company list…
        </div>
      )}

      {loading && !data && (
        <div className="card flex items-center justify-center gap-2 py-24 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          Loading matrix…
        </div>
      )}

      {isSummaryMode && summaryData && !summaryLoading && (
        <div className="card p-5 shadow-sm space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Period</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{summaryData.period_label}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Completed Runs</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{summaryData.total_runs}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Companies With Data</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{summaryData.unique_company_count}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">View Mode</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">Pick company, then load detail</div>
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            First select a company with consolidation data for this period. The detailed report opens only after you click a company, so the page stays fast even when the period has very large runs.
          </div>

          <div className="space-y-3">
            {summaryData.runs.map((run) => (
              <div key={run.run_id} className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Run #{run.run_id}</div>
                    <div className="text-xs text-gray-500">Completed {formatTimestamp(run.completed_at)}</div>
                  </div>
                  <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                    {run.entity_count} companies
                  </div>
                </div>

                {run.is_large ? (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    This run is very large. Use the entity selector in the page header to narrow it before opening the detailed report.
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {run.companies.map((company) => (
                      <button
                        key={`${run.run_id}-${company.id}`}
                        type="button"
                        onClick={() => {
                          setSelectedCompanyId(String(company.id));
                          setError(null);
                        }}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-900"
                      >
                        <Building2 className="h-4 w-4 flex-shrink-0 text-primary-600" />
                        <span>{company.code} — {company.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {run.hidden_company_count > 0 && (
                  <div className="mt-3 text-xs text-gray-500">
                    {run.hidden_company_count} more companies are hidden for readability.
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data && theadRows && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm">
            <div>
              {data.title} — Scenario {data.scenario} — Period {data.period_label} — Run #{data.run_id}
            </div>
            <div className="flex items-center gap-2">
              {!scopeEntityId && selectedCompanyId && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCompanyId(null);
                    setData(null);
                    setError(null);
                  }}
                  className="inline-flex min-h-11 items-center gap-2 rounded-md border border-white/30 bg-white/10 px-3 py-2 text-xs font-medium text-white transition hover:bg-white/20"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to company list
                </button>
              )}
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex min-h-11 items-center gap-2 rounded-md border border-white/30 bg-white/10 px-3 py-2 text-xs font-medium text-white transition hover:bg-white/20 disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export Excel
              </button>
            </div>
          </div>

          <div className="card p-5 shadow-sm">
            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Selected Company</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {selectedCompany ? `${selectedCompany.code} — ${selectedCompany.name}` : 'Scoped selection'}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Reporting Currency</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{data.reporting_currency}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Entities</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{data.entities.length}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Adjusted Rows</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{summary.adjustedRows}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Non-zero Rows</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{summary.nonZeroRows}</div>
              </div>
            </div>

            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-700">
              Compact report view:
              {' '}Local standalone, translated standalone, proportion, IC elimination, NCI, contribution, and final consolidated balance.
            </div>

            <div className="mb-4 grid gap-3 lg:grid-cols-[minmax(220px,1.5fr)_220px_auto]">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Account Search
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search code or account name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="space-y-2 pt-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showOnlyNonZero}
                    onChange={(e) => setShowOnlyNonZero(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Show only non-zero rows
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showOnlyAdjusted}
                    onChange={(e) => setShowOnlyAdjusted(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Show only adjusted rows
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={isDetailedView}
                    onChange={(e) => setIsDetailedView(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Detailed View
                </label>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-max w-full border-collapse text-sm">
                <thead>
                  <tr>{theadRows.row1}</tr>
                  <tr>{theadRows.row2}</tr>
                </thead>
                <tbody>
                  <HierarchyTreeBody
                    tree={filteredTree}
                    entities={data.entities}
                    visibleColumns={visibleColumns}
                    expandedNodes={expandedNodes}
                    onToggle={toggleNode}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── HierarchyTreeBody ────────────────────────────────────────────────────────
// Renders Balance Sheet roots then Income Statement roots, each preceded by a
// section banner row identical to the previous flat implementation.

type VisibleColumns = typeof DETAIL_COLUMNS;

function HierarchyTreeBody({
  tree,
  entities,
  visibleColumns,
  expandedNodes,
  onToggle,
}: {
  tree: ConsolTreeNode[];
  entities: ConsolidationReviewMatrix['entities'];
  visibleColumns: VisibleColumns;
  expandedNodes: Set<number>;
  onToggle: (id: number) => void;
}) {
  const colSpan = 1 + entities.length * visibleColumns.length + 1;
  const bsRoots = tree.filter((n) => n.isBalanceSheet);
  const isRoots = tree.filter((n) => !n.isBalanceSheet);

  return (
    <>
      {bsRoots.length > 0 && (
        <tr className="bg-gray-100">
          <td
            colSpan={colSpan}
            className="border border-gray-200 px-3 py-2 text-xs font-bold uppercase tracking-wide text-gray-700"
          >
            Balance sheet — detail
          </td>
        </tr>
      )}
      {bsRoots.map((node) => (
        <HierarchyRow
          key={node.id}
          node={node}
          entities={entities}
          visibleColumns={visibleColumns}
          expandedNodes={expandedNodes}
          onToggle={onToggle}
        />
      ))}

      {isRoots.length > 0 && (
        <tr className="bg-gray-100">
          <td
            colSpan={colSpan}
            className="border border-gray-200 px-3 py-2 text-xs font-bold uppercase tracking-wide text-gray-700"
          >
            Income statement — detail
          </td>
        </tr>
      )}
      {isRoots.map((node) => (
        <HierarchyRow
          key={node.id}
          node={node}
          entities={entities}
          visibleColumns={visibleColumns}
          expandedNodes={expandedNodes}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}

// ─── HierarchyRow ─────────────────────────────────────────────────────────────
// Single account row with depth-based indentation and ▶/▼ expand toggle.
// Parent rows show aggregated subtotals; leaf rows show their own values.
// Recursively renders visible children when the node is expanded.

function HierarchyRow({
  node,
  entities,
  visibleColumns,
  expandedNodes,
  onToggle,
}: {
  node: ConsolTreeNode;
  entities: ConsolidationReviewMatrix['entities'];
  visibleColumns: VisibleColumns;
  expandedNodes: Set<number>;
  onToggle: (id: number) => void;
}) {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children.length > 0;
  const isSection = node.depth === 0 && !node.isLeaf;
  const isSubtotal = !node.isLeaf;

  // The sticky account cell needs an explicit bg that matches the row bg
  const stickyBg = isSection ? 'bg-slate-100' : isSubtotal ? 'bg-gray-50' : 'bg-white';

  return (
    <>
      <tr
        className={clsx(
          'hover:bg-gray-50/80',
          isSection && 'bg-slate-100',
          isSubtotal && !isSection && 'bg-gray-50',
        )}
      >
        {/* Account cell — sticky left column with depth indentation */}
        <td
          className={clsx(
            'sticky left-0 z-20 w-[180px] min-w-[150px] max-w-[180px] border border-gray-200 px-2 py-1.5 text-left align-top',
            stickyBg,
          )}
          style={{ paddingLeft: `${node.depth * 16 + 8}px` }}
        >
          <div className="flex items-center gap-1">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onToggle(node.id)}
                className="flex-shrink-0 w-3 text-[9px] text-center leading-none text-gray-500 hover:text-gray-900"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            ) : (
              <span className="w-3 flex-shrink-0" />
            )}
            <div className="space-y-0.5 min-w-0">
              {node.code && (
                <div className="font-mono text-[10px] leading-4 text-gray-500 break-all">
                  {node.code}
                </div>
              )}
              <div
                className={clsx(
                  'text-xs leading-4 break-words',
                  isSection
                    ? 'font-bold uppercase tracking-wide text-gray-800'
                    : isSubtotal
                    ? 'font-semibold text-gray-900'
                    : 'text-gray-800',
                )}
              >
                {node.name}
              </div>
            </div>
          </div>
        </td>

        {/* Data cells — entities × visible columns */}
        {entities.flatMap((ent) =>
          visibleColumns.map((col) => {
            const ev = node.values.byEntity[String(ent.id)];
            const val = ev ? (ev[col.key as keyof EntityNumericValues] ?? 0) : 0;
            const cellBg = GROUP_CELL_BG[col.groupKey] ?? '';
            return (
              <td
                key={`${ent.id}-${col.key}`}
                className={clsx(
                  'border border-gray-200 px-2 py-1 text-center font-mono text-xs tabular-nums text-gray-800',
                  cellBg,
                  isSubtotal && 'font-semibold',
                )}
              >
                {fmtCell(val)}
              </td>
            );
          }),
        )}

        {/* Consolidated total column */}
        <td
          className={clsx(
            'border border-gray-200 px-2 py-1 text-center font-mono text-xs tabular-nums',
            isSection
              ? 'bg-gray-200 font-bold text-gray-900'
              : 'bg-gray-100 font-semibold text-gray-900',
          )}
        >
          {fmtCell(node.values.consolidated)}
        </td>
      </tr>

      {/* Recursively render children when expanded */}
      {isExpanded &&
        node.children.map((child) => (
          <HierarchyRow
            key={child.id}
            node={child}
            entities={entities}
            visibleColumns={visibleColumns}
            expandedNodes={expandedNodes}
            onToggle={onToggle}
          />
        ))}
    </>
  );
}
